// frontend/packages/business-console/src/pages/Pulse/demoData.js
//
// Frontend-only demo fixtures for the Pulse page. Activated via the
// `?demo=1` query string — used for marketing screenshots without
// touching real tenant data (which is Joshua's live business and
// can't be public). Numbers are realistic for an SMB field-service
// tenant doing roughly $25–35k/mo across 60–80 active customers.
//
// Update freely — this file is the source of truth for what the
// marketing screenshots show.

// Currency helper shared by sections. Keeps formatting consistent.
export function fmtUsd(cents) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format((cents || 0) / 100);
}

export function fmtUsdPrecise(cents) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format((cents || 0) / 100);
}

// ---------- Money ----------
//
// Outstanding (sent + viewed + partial + overdue) and paid-this-period
// summaries. Recent payments list = 5 most recent succeeded payments.

export const demoMoney = {
  outstandingCents: 1247500, // $12,475 outstanding across all unpaid invoices
  overdueCents: 215000, // $2,150 overdue (subset of outstanding)
  outstandingCount: 14,
  overdueCount: 2,
  paidThisWeekCents: 982000, // $9,820 paid in last 7 days
  paidThisWeekCount: 8,
  paidThisMonthCents: 3145000, // $31,450 month-to-date
  paidThisMonthCount: 27,
  avgTicketCents: 116480, // $1,164.80 average ticket
  paidPriorMonthCents: 2872000, // for delta vs prior month
  recent: [
    {
      id: 'p1',
      invoiceId: 'demo-inv-0184',
      customerName: 'Acme Roofing LLC',
      invoiceNumber: 'INV-0184',
      amountCents: 245000,
      method: 'card',
      paidAt: '2026-05-26T14:22:00Z',
    },
    {
      id: 'p2',
      invoiceId: 'demo-inv-0183',
      customerName: 'Sarah Kim',
      invoiceNumber: 'INV-0183',
      amountCents: 89500,
      method: 'card',
      paidAt: '2026-05-26T11:08:00Z',
    },
    {
      id: 'p3',
      invoiceId: 'demo-inv-0181',
      customerName: 'Henderson Group',
      invoiceNumber: 'INV-0181',
      amountCents: 137500,
      method: 'ach',
      paidAt: '2026-05-25T16:45:00Z',
    },
    {
      id: 'p4',
      invoiceId: 'demo-inv-0180',
      customerName: 'Wagner Hospitality',
      invoiceNumber: 'INV-0180',
      amountCents: 312000,
      method: 'card',
      paidAt: '2026-05-25T09:30:00Z',
    },
    {
      id: 'p5',
      invoiceId: 'demo-inv-0179',
      customerName: "Madelyn O'Brien",
      invoiceNumber: 'INV-0179',
      amountCents: 68000,
      method: 'card',
      paidAt: '2026-05-24T18:55:00Z',
    },
  ],
};

// ---------- Pipeline (quotes funnel) ----------
//
// Stage counts + recent activity. Conversion rate = accepted /
// (accepted + declined + expired) over the trailing 30 days.

export const demoPipeline = {
  draftCount: 4,
  sentCount: 9,
  viewedCount: 6,
  acceptedCount: 11,
  declinedCount: 2,
  expiredCount: 3,
  conversionPct: 69, // 11 / (11 + 2 + 3) — accepted vs total resolved
  conversionDeltaPct: 8, // +8pp vs prior 30 days
  avgQuoteValueCents: 184500, // $1,845 average quote value
  recentActivity: [
    {
      id: 'q1',
      action: 'accepted',
      quoteNumber: 'Q-0091',
      customerName: 'Acme Roofing LLC',
      amountCents: 245000,
      at: '2026-05-26T13:14:00Z',
    },
    {
      id: 'q2',
      action: 'viewed',
      quoteNumber: 'Q-0093',
      customerName: 'Maria Santos',
      amountCents: 67500,
      at: '2026-05-26T10:02:00Z',
    },
    {
      id: 'q3',
      action: 'sent',
      quoteNumber: 'Q-0094',
      customerName: 'Tilbury Construction',
      amountCents: 421000,
      at: '2026-05-26T09:18:00Z',
    },
    {
      id: 'q4',
      action: 'expired',
      quoteNumber: 'Q-0082',
      customerName: 'Pinewood Estates HOA',
      amountCents: 158000,
      at: '2026-05-26T00:01:00Z',
    },
    {
      id: 'q5',
      action: 'accepted',
      quoteNumber: 'Q-0090',
      customerName: 'Wagner Hospitality',
      amountCents: 312000,
      at: '2026-05-25T15:40:00Z',
    },
  ],
};

// ---------- CRM ----------

export const demoCrm = {
  totalCustomers: 74,
  newThisMonth: 9,
  activeLast30: 41, // had a touchpoint (message, quote, invoice) in 30 days
  stageBreakdown: [
    { stage: 'lead', count: 8 },
    { stage: 'qualified', count: 6 },
    { stage: 'customer', count: 49 },
    { stage: 'repeat', count: 11 },
  ],
};

// Customer rows for the CRM table under the demo flag. Shape mirrors the
// listCustomers response (incl. lifetimeValueCents from the enrichment).
const daysAgoIso = (n) =>
  new Date(Date.now() - n * 24 * 60 * 60 * 1000).toISOString();
export const demoCustomers = [
  {
    _id: 'demo-c1',
    firstName: 'Renée',
    lastName: 'Bautista',
    email: 'renee.b@gmail.com',
    phone: '(555) 311-0042',
    stage: 'customer',
    lifetimeValueCents: 660000,
    lastContactedAt: daysAgoIso(0),
    personId: null,
  },
  {
    _id: 'demo-c2',
    firstName: 'Pinewood Estates',
    lastName: 'HOA',
    email: 'ops@pinewoodhoa.org',
    phone: '(555) 778-1200',
    stage: 'repeat',
    lifetimeValueCents: 1850000,
    lastContactedAt: daysAgoIso(2),
    personId: 'demo-p1',
  },
  {
    _id: 'demo-c3',
    firstName: 'Jordan',
    lastName: 'Whitfield',
    email: 'jordan.w@gmail.com',
    phone: '(555) 482-1199',
    stage: 'lead',
    lifetimeValueCents: 0,
    lastContactedAt: daysAgoIso(0),
    personId: null,
  },
  {
    _id: 'demo-c4',
    firstName: 'Carl',
    lastName: 'Beaumont',
    email: 'carl.beaumont@outlook.com',
    phone: '(555) 904-3321',
    stage: 'qualified',
    lifetimeValueCents: 275000,
    lastContactedAt: daysAgoIso(0),
    personId: null,
  },
  {
    _id: 'demo-c5',
    firstName: 'Maria',
    lastName: 'Santos',
    email: 'maria.santos@gmail.com',
    phone: '(555) 220-8842',
    stage: 'customer',
    lifetimeValueCents: 118000,
    lastContactedAt: daysAgoIso(2),
    personId: null,
  },
  {
    _id: 'demo-c6',
    firstName: 'Greenfield',
    lastName: 'Cafe',
    email: 'hello@greenfieldcafe.com',
    phone: '(555) 661-7788',
    stage: 'repeat',
    lifetimeValueCents: 690000,
    lastContactedAt: daysAgoIso(11),
    personId: null,
  },
  {
    _id: 'demo-c7',
    firstName: 'Acme Roofing',
    lastName: 'LLC',
    email: 'billing@acmeroofing.com',
    phone: '(555) 145-9930',
    stage: 'customer',
    lifetimeValueCents: 920000,
    lastContactedAt: daysAgoIso(4),
    personId: null,
  },
  {
    _id: 'demo-c8',
    firstName: 'Tilbury',
    lastName: 'Construction',
    email: 'jobs@tilburyco.com',
    phone: '(555) 388-2042',
    stage: 'qualified',
    lifetimeValueCents: 421000,
    lastContactedAt: daysAgoIso(6),
    personId: null,
  },
  {
    _id: 'demo-c9',
    firstName: 'Dale',
    lastName: 'Whitmore',
    email: 'dwhitmore@gmail.com',
    phone: '(555) 510-7781',
    stage: 'lost',
    lifetimeValueCents: 0,
    lastContactedAt: daysAgoIso(17),
    personId: null,
  },
];

// ---------- Inbox ----------

export const demoInbox = {
  unreadCount: 3,
  totalLast7Days: 17,
  recent: [
    {
      id: 'm1',
      sender: 'Jordan Whitfield',
      preview: 'Hey — can I get an estimate for the back deck job…',
      at: '2026-05-26T14:51:00Z',
      unread: true,
    },
    {
      id: 'm2',
      sender: 'Renée Bautista',
      preview: 'Thanks for the follow-up. Yes, please go ahead with…',
      at: '2026-05-26T11:42:00Z',
      unread: true,
    },
    {
      id: 'm3',
      sender: 'Carl Beaumont',
      preview: 'Quick question on the warranty — does it cover…',
      at: '2026-05-26T09:15:00Z',
      unread: true,
    },
  ],
};

// ---------- AI Visibility (UAVS) ----------

export const demoVisibility = {
  score: 78, // out of 100
  deltaPoints: 4, // vs last scan
  lastScanAt: '2026-05-25T08:00:00Z',
  topIssues: [
    { id: 'i1', label: 'Add missing FAQ schema', severity: 'medium' },
    { id: 'i2', label: 'Update business hours on Google', severity: 'high' },
    { id: 'i3', label: '3 broken links on /services', severity: 'low' },
  ],
};

// ---------- Tasks ----------

// Shape mirrors `TasksPulse` in `backend/crm/lib/pulseAggregator.ts` —
// each row is one of the four task kinds shipped in PR B.
export const demoTasks = {
  openCount: 4,
  overdueCount: 2,
  recent: [
    {
      id: 'inv:demo-inv-overdue-1',
      kind: 'overdue_invoice',
      title: 'INV-0182 · Tilbury Construction',
      subtitle: '$4,200.00 due',
      dueAt: '2026-05-20T17:00:00Z',
      overdue: true,
      href: '/invoices/demo-inv-overdue-1',
    },
    {
      id: 'p:demo-pay-failed-1',
      kind: 'failed_payment',
      title: 'Payment failed — Henderson Group',
      subtitle: '$1,375.00 · INV-0181 · insufficient_funds',
      dueAt: '2026-05-25T16:45:00Z',
      overdue: true,
      href: '/invoices/demo-inv-0181',
    },
    {
      id: 'q:demo-q-expiring-1',
      kind: 'expiring_quote',
      title: 'Q-0143 · Sarah Kim',
      subtitle: '$2,800.00 · expires soon',
      dueAt: '2026-05-29T17:00:00Z',
      overdue: false,
      href: '/quotes/demo-q-expiring-1',
    },
    {
      id: 'q:demo-q-expiring-2',
      kind: 'expiring_quote',
      title: 'Q-0141 · Jordan Whitfield',
      subtitle: '$6,150.00 · expires soon',
      dueAt: '2026-06-02T17:00:00Z',
      overdue: false,
      href: '/quotes/demo-q-expiring-2',
    },
  ],
};

// ---------- Performance ----------

export const demoPerformance = {
  status: 'operational', // operational | degraded | down
  uptimePct7d: 99.97,
  avgResponseMs: 184,
  pageviewsLast7d: 2_843,
  pageviewsDeltaPct: 12, // vs prior 7 days
  alertsLast7d: 0,
};

// ---------- Connect status (payments capability) ----------

export const demoConnect = {
  onboarded: true,
  chargesEnabled: true,
  payoutsEnabled: true,
  nextPayoutAt: '2026-05-28T08:00:00Z',
  nextPayoutCents: 612000, // $6,120 — net to client after fees on this cycle
  applicationFeeBps: 200,
};

// ---------- Payments dashboard (Page 13) ----------
//
// Three shapes mirroring the page's three loads, so `?demo=1` lights up
// the whole Payments dashboard without a Stripe account:
//
//   demoPaymentsAccount   — getOrCreateStripeConnectAccount (status pill)
//   demoPaymentsOverview  — getStripeConnectPayoutOverview (balance hero,
//                           payouts card)
//   demoPaymentsDashboard — getCrmPaymentsDashboard (stat pairs, recent
//                           payments table, dispute tiles)
//
// Numbers are coherent with the same SMB tenant story used elsewhere in
// this file (≈ $31k/mo collected, ~2% platform fee, daily payouts).

export const demoPaymentsAccount = {
  chargesEnabled: true,
  payoutsEnabled: true,
  detailsSubmitted: true,
  applicationFeeBps: 200,
};

export const demoPaymentsOverview = {
  availableCents: 418300, // $4,183 available now
  inTransitCents: 612000, // $6,120 on the way to the bank
  instantAvailableCents: 418300, // all available is instant-eligible
  payoutSchedule: 'daily',
  nextPayout: {
    amountCents: 612000,
    arrivalDate: '2026-05-28T08:00:00Z',
  },
  bank: { bankName: 'Chase', last4: '8842' },
  recentPayouts: [
    {
      id: 'po1',
      amountCents: 588000,
      status: 'paid',
      createdAt: '2026-05-24T08:00:00Z',
    },
    {
      id: 'po2',
      amountCents: 471500,
      status: 'paid',
      createdAt: '2026-05-23T08:00:00Z',
    },
    {
      id: 'po3',
      amountCents: 633200,
      status: 'paid',
      createdAt: '2026-05-22T08:00:00Z',
    },
    {
      id: 'po4',
      amountCents: 412800,
      status: 'pending',
      createdAt: '2026-05-21T08:00:00Z',
    },
  ],
};

export const demoPaymentsDashboard = {
  totals: {
    collectedCents: 3145000, // $31,450 collected (matches demoMoney month-to-date)
    netToClientCents: 3061700, // after Stripe + platform fees
    stripeFeeCents: 20300, // ≈ Stripe processing
    applicationFeeCents: 63000, // 2% platform fee on $31,450
  },
  avgTimeToPayDays: 3,
  priorAvgTimeToPayDays: 5,
  refunds: { count: 1, amountCents: 12500 },
  openDisputes: 0,
  recent: [
    {
      id: 'p1',
      invoiceId: 'demo-inv-0184',
      invoiceNumber: 'INV-0184',
      customerName: 'Acme Roofing LLC',
      amountCents: 245000,
      netCents: 238200,
      method: 'card',
      state: 'succeeded',
      paidAt: '2026-05-26T14:22:00Z',
    },
    {
      id: 'p2',
      invoiceId: 'demo-inv-0183',
      invoiceNumber: 'INV-0183',
      customerName: 'Sarah Kim',
      amountCents: 89500,
      netCents: 86700,
      method: 'card',
      state: 'succeeded',
      paidAt: '2026-05-26T11:08:00Z',
    },
    {
      id: 'p3',
      invoiceId: 'demo-inv-0181',
      invoiceNumber: 'INV-0181',
      customerName: 'Henderson Group',
      amountCents: 137500,
      netCents: 134900,
      method: 'ach',
      state: 'succeeded',
      paidAt: '2026-05-25T16:45:00Z',
    },
    {
      id: 'p4',
      invoiceId: 'demo-inv-0180',
      invoiceNumber: 'INV-0180',
      customerName: 'Wagner Hospitality',
      amountCents: 312000,
      netCents: 303400,
      method: 'card',
      state: 'succeeded',
      paidAt: '2026-05-25T09:30:00Z',
    },
    {
      id: 'p5',
      invoiceId: 'demo-inv-0179',
      invoiceNumber: 'INV-0179',
      customerName: "Madelyn O'Brien",
      amountCents: 68000,
      netCents: 12500,
      method: 'card',
      state: 'partially_refunded',
      refundedCents: 12500,
      paidAt: '2026-05-24T18:55:00Z',
    },
  ],
};

// ---------- Visitors (site analytics) ----------
//
// Two shapes:
//
//   demoVisitors           — what VisitorsSection (Pulse card) renders
//                            (today + yesterday + 14d sparkline)
//   demoVisitorsPage       — what the full Visitors deep-dive page renders
//                            ({ summary, timeseries, funnel, topPages,
//                              sources, ai })
//
// Designed to tell a coherent story across both surfaces: an HVAC tenant
// running a Facebook ad to a "Free Audit" landing page, getting AI-
// crawler hits, with a healthy conversion rate.

function isoDay(daysAgo) {
  const d = new Date(Date.now() - daysAgo * 86400000);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// 14-day session sparkline. Slight upward trend that ramps when FB ad
// "turns on" mid-range, with weekend dips.
const VISITORS_SPARKLINE_VALUES = [
  62, 71, 58, 84, 79, 64, 41, 95, 132, 168, 154, 188, 142, 197,
];

export const demoVisitors = {
  today: {
    sessions: 87,
    uniqueVisitors: 73,
    pageViews: 142,
    formStarts: 11,
    formSubmits: 6,
    phoneTaps: 4,
  },
  yesterday: {
    sessions: 197,
    pageViews: 412,
    formSubmits: 13,
    conversionRate: 0.066,
    aiCrawlerHits: 38,
  },
  sparkline: VISITORS_SPARKLINE_VALUES.map((sessions, i) => ({
    date: isoDay(13 - i),
    sessions,
  })),
};

// Full Visitors page (the deep-dive). Each key corresponds to one of the
// six visitorsService.js getters.
export const demoVisitorsPage = {
  summary: demoVisitors,

  timeseries: {
    days: 14,
    items: VISITORS_SPARKLINE_VALUES.map((sessions, i) => ({
      date: isoDay(13 - i),
      sessions,
      uniqueVisitors: Math.round(sessions * 0.82),
      pageViews: Math.round(sessions * 2.1),
      bounceRate: 0.34 + (i % 3) * 0.02,
      formSubmits: Math.max(0, Math.round(sessions * 0.065)),
      conversionRate: 0.05 + (i % 4) * 0.008,
      phoneTaps: Math.round(sessions * 0.04),
    })),
  },

  funnel: {
    days: 14,
    sessions: 1639,
    scrolled75: 894,
    formStarts: 234,
    formAttempts: 118,
    formSubmits: 89,
  },

  topPages: {
    days: 14,
    items: [
      { path: '/', views: 1842 },
      { path: '/free-audit', views: 1207 },
      { path: '/services', views: 612 },
      { path: '/services/hvac-installation', views: 418 },
      { path: '/about', views: 287 },
      { path: '/contact', views: 254 },
      { path: '/services/repair', views: 196 },
      { path: '/financing', views: 142 },
      { path: '/reviews', views: 98 },
      { path: '/blog/when-to-replace-your-hvac', views: 73 },
    ],
  },

  sources: {
    days: 14,
    referrers: [
      { ref: 'facebook.com', sessions: 612 },
      { ref: '(direct)', sessions: 387 },
      { ref: 'google.com', sessions: 294 },
      { ref: 'duckduckgo.com', sessions: 78 },
      { ref: 'bing.com', sessions: 42 },
      { ref: 'nextdoor.com', sessions: 31 },
    ],
    utm: [
      {
        source: 'facebook',
        medium: 'cpc',
        campaign: 'free-audit-q2',
        sessions: 487,
        formSubmits: 42,
      },
      {
        source: 'facebook',
        medium: 'cpc',
        campaign: 'summer-tuneup',
        sessions: 218,
        formSubmits: 14,
      },
      {
        source: 'google',
        medium: 'cpc',
        campaign: 'brand-search',
        sessions: 96,
        formSubmits: 11,
      },
      {
        source: 'newsletter',
        medium: 'email',
        campaign: 'spring-promo',
        sessions: 47,
        formSubmits: 7,
      },
    ],
  },

  ai: {
    days: 14,
    totalHits: 312,
    totalTraining: 218,
    totalRuntime: 94,
    byBot: [
      { name: 'GPTBot', hits: 124 },
      { name: 'ChatGPT', hits: 58 },
      { name: 'ClaudeBot', hits: 46 },
      { name: 'PerplexityBot', hits: 32 },
      { name: 'Google Extended', hits: 22 },
      { name: 'Perplexity', hits: 14 },
      { name: 'Common Crawl', hits: 9 },
      { name: 'Bytespider', hits: 7 },
    ],
    sparkline: VISITORS_SPARKLINE_VALUES.map((_, i) => ({
      date: isoDay(13 - i),
      hits: 12 + Math.round(Math.sin(i / 2) * 8 + i * 1.5),
    })),
  },
};

// ---------- Top-level aggregator ----------

export const demoPulse = {
  money: demoMoney,
  pipeline: demoPipeline,
  crm: demoCrm,
  inbox: demoInbox,
  visibility: demoVisibility,
  tasks: demoTasks,
  performance: demoPerformance,
  connect: demoConnect,
  visitors: demoVisitors,
};
